import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import GenerateDownloadLinkModal from './GenerateDownloadLinkModal';

/** subservices.short_name = 'WLAN'. Matches routers/offensive.WIRELESS_SUBTYPE_ID. */
const WIRELESS_SUBTYPE_ID = 20;

const TOOL_PROFILES = [
  {
    id: 'wireless',
    label: 'Wireless',
    packages: 'airgeddon, eaphammer, hostapd-wpe, hcxdumptool, hcxtools, mdk4, bettercap, pixiewps, cowpatty'
  }
];

const AgentManagementConsolidatedModal = ({ engagement, onClose, onAgentBuilt }) => {
  const [activeTab, setActiveTab] = useState('manage'); // 'create' or 'manage'
  const pollIntervalRef = useRef(null);
  const buildPollIntervalRef = useRef(null);
  
  // ==================== MANAGE TAB STATE ====================
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDownloadLinkModal, setShowDownloadLinkModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [expandedHashes, setExpandedHashes] = useState(new Set());

  // ==================== CREATE TAB STATE (from BuildAgentModal) ====================
  // Agent Configuration State
  const [agentName, setAgentName] = useState(
    `kingpin-${engagement.customer?.short_name?.toLowerCase() || 'client'}-${engagement.id}-${Date.now()}`
  );
  const [vmProvider, setVmProvider] = useState('vmware');
  const [osTemplate, setOsTemplate] = useState('kali');
  
  // Build result state (credentials + download)
  const [buildResult, setBuildResult] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [credentialsSaved, setCredentialsSaved] = useState(false);
  
  // VM Resources
  const [cpuCores, setCpuCores] = useState(4);
  const [ramGb, setRamGb] = useState(8);
  const [diskGb, setDiskGb] = useState(50);
  
  // Network Configuration
  const [networkMode, setNetworkMode] = useState('dhcp');
  const [staticIp, setStaticIp] = useState('');
  const [staticNetmask, setStaticNetmask] = useState('255.255.255.0');
  const [staticGateway, setStaticGateway] = useState('');
  const [dnsServers, setDnsServers] = useState('8.8.8.8, 8.8.4.4');
  
  // OpenVPN Configuration
  const [vpnEnabled, setVpnEnabled] = useState(true);
  const [vpnServer, setVpnServer] = useState('queenpin.mindcore.es');
  const [vpnPort, setVpnPort] = useState(8443);
  const [vpnProtocol, setVpnProtocol] = useState('tcp');
  const [availableVpnIps, setAvailableVpnIps] = useState([]);
  const [selectedVpnIp, setSelectedVpnIp] = useState('');
  const [loadingVpnIps, setLoadingVpnIps] = useState(false);
  
  // Agent Settings
  // A wireless engagement's agent exists to carry a radio, so the wireless
  // toolset is ticked by default there. Building one without it produces a VM
  // that cannot survey, which is only discovered after the ~40 minute build.
  const [toolProfiles, setToolProfiles] = useState(
    engagement?.subtype_id === WIRELESS_SUBTYPE_ID ? ['wireless'] : []
  );
  const [autoStart, setAutoStart] = useState(true);
  const [persistentStorage, setPersistentStorage] = useState(true);
  const [snapshotsEnabled, setSnapshotsEnabled] = useState(true);
  
  // Deployment Status
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildStatus, setBuildStatus] = useState('');
  const [buildProgress, setBuildProgress] = useState(0);
  
  // Notification State
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');

  const showNotif = (message, type = 'success') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    if (type === 'success') {
      setTimeout(() => setShowNotification(false), 3000);
    }
  };

  // ==================== MANAGE TAB FUNCTIONS ====================
  useEffect(() => {
    if (activeTab === 'manage') {
      fetchAgents(true);
      
      pollIntervalRef.current = setInterval(() => {
        fetchAgents(false);
      }, 5000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [engagement.id, activeTab]);

  // Cleanup build polling on unmount
  useEffect(() => {
    return () => {
      if (buildPollIntervalRef.current) {
        clearInterval(buildPollIntervalRef.current);
      }
    };
  }, []);

  const fetchAgents = async (showLoadingSpinner = true) => {
    if (showLoadingSpinner) {
      setIsLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${Config.APIBaseURL}/engagement/${engagement.id}/agents`,
        {
          headers: { 'access_token': `Bearer ${token}` }
        }
      );
      console.log('[AgentManagementConsolidatedModal] Fetched agents:', response.data);
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      if (showLoadingSpinner) {
        alert(`Error: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      if (showLoadingSpinner) {
        setIsLoading(false);
      }
    }
  };

  const handleDownload = async (agent) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${Config.APIBaseURL}/agent/${agent.id}/download-link`,
        {
          customer_email: null,
          customer_name: null,
          expires_hours: 1,
          max_downloads: 1,
          send_email: false,
          notes: 'Authenticated user download'
        },
        {
          headers: { 'access_token': `Bearer ${token}` }
        }
      );

      const downloadUrl = response.data.download_url;
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${agent.agent_name}.${agent.vm_format || 'ova'}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`[AgentManagementConsolidatedModal] Initiated download for agent: ${agent.agent_name}`);
    } catch (error) {
      console.error('Error downloading agent:', error);
      alert(`Download error: ${error.response?.data?.detail || error.message || 'Network error'}`);
    }
  };

  const handleDelete = async (agent) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${Config.APIBaseURL}/agent/${agent.id}`,
        {
          headers: { 'access_token': `Bearer ${token}` }
        }
      );
      
      await fetchAgents(true);
      setShowDeleteConfirm(false);
      setAgentToDelete(null);
    } catch (error) {
      console.error('Error deleting agent:', error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'text-emerald-400 bg-emerald-950/30 border-emerald-500/30';
      case 'building': return 'text-blue-400 bg-blue-950/30 border-blue-500/30';
      case 'error': return 'text-rose-400 bg-rose-950/30 border-rose-500/30';
      case 'active': return 'text-green-400 bg-green-950/30 border-green-500/30';
      case 'offline': return 'text-yellow-400 bg-yellow-950/30 border-yellow-500/30';
      case 'retired': return 'text-slate-400 bg-slate-950/30 border-slate-500/30';
      default: return 'text-slate-400 bg-slate-950/30 border-slate-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'building':
        return (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'error':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'active':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'offline':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        );
    }
  };

  const getDisplayStatus = (agent) => {
    if (agent.status === 'active' && agent.is_online === false) {
      return 'offline';
    }
    return agent.status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatHash = (hash, agentId) => {
    if (!hash) return 'N/A';
    const isExpanded = expandedHashes.has(agentId);
    if (isExpanded) {
      return hash;
    }
    return `${hash.substring(0, 4)}...${hash.substring(hash.length - 4)}`;
  };

  const toggleHashExpansion = (agentId) => {
    setExpandedHashes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
  };

  // ==================== CREATE TAB FUNCTIONS (from BuildAgentModal) ====================
  // Fetch available VPN IPs from backend
  useEffect(() => {
    if (activeTab === 'create' && vpnEnabled) {
      fetchAvailableVpnIps();
    }
  }, [vpnEnabled, activeTab]);

  const fetchAvailableVpnIps = async () => {
    setLoadingVpnIps(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${Config.APIBaseURL}/agent/vpn-ips/available`, {
        headers: {
          'access_token': `Bearer ${token}`
        }
      });

      if (response.data && response.data.available_ips) {
        setAvailableVpnIps(response.data.available_ips);
        if (response.data.available_ips.length > 0) {
          setSelectedVpnIp(response.data.available_ips[0]);
        }
      }
    } catch (error) {
      console.warn('Could not fetch available VPN IPs, using fallback:', error.message);
      const fallbackIps = [
        '10.8.0.2',
        '10.8.0.3',
        '10.8.0.4',
        '10.8.0.5',
        '10.8.0.6'
      ];
      setAvailableVpnIps(fallbackIps);
      setSelectedVpnIp(fallbackIps[0]);
    } finally {
      setLoadingVpnIps(false);
    }
  };

  const handleBuildAgent = async () => {
    // Validation
    if (!agentName.trim()) {
      showNotif('Please enter an agent name', 'error');
      return;
    }
    
    if (vpnEnabled && !vpnServer.trim()) {
      showNotif('Please enter the VPN server', 'error');
      return;
    }

    if (vpnEnabled && !selectedVpnIp) {
      showNotif('Please select a VPN IP', 'error');
      return;
    }

    if (networkMode === 'static') {
      if (!staticIp.trim() || !staticGateway.trim()) {
        showNotif('Please complete the static IP configuration', 'error');
        return;
      }
    }

    setIsBuilding(true);
    setBuildStatus('Initializing configuration...');
    setBuildProgress(10);

    try {
      const token = localStorage.getItem('token');
      const agentConfig = {
        engagement_id: engagement.id,
        agent_name: agentName,
        vm_config: {
          provider: vmProvider,
          os_template: osTemplate,
          resources: {
            cpu_cores: cpuCores,
            ram_gb: ramGb,
            disk_gb: diskGb
          },
          auto_start: autoStart,
          persistent_storage: persistentStorage,
          snapshots_enabled: snapshotsEnabled,
          tool_profiles: toolProfiles
        },
        network_config: {
          mode: networkMode,
          ...(networkMode === 'static' && {
            static_ip: staticIp,
            netmask: staticNetmask,
            gateway: staticGateway,
            dns_servers: dnsServers.split(',').map(s => s.trim())
          })
        },
        vpn_config: vpnEnabled ? {
          server: vpnServer,
          port: vpnPort,
          protocol: vpnProtocol,
          assigned_ip: selectedVpnIp
        } : null
      };

      const response = await axios.post(`${Config.APIBaseURL}/agent/build`, agentConfig, {
        headers: {
          'access_token': `Bearer ${token}`
        }
      });

      const result = response.data;
      
      console.log('[AgentManagementConsolidatedModal] Initial build response:', result);
      
      setBuildResult(result);
      setBuildStatus(`Build started`);
      setBuildProgress(result.build_progress || 10);
      
      // Start polling for build status updates
      buildPollIntervalRef.current = setInterval(async () => {
        try {
          const statusResponse = await axios.get(
            `${Config.APIBaseURL}/agent/${result.agent_id}`,
            {
              headers: { 'access_token': `Bearer ${token}` }
            }
          );
          
          const statusData = statusResponse.data;
          
          console.log('[AgentManagementConsolidatedModal] Poll response:', statusData);
          
          setBuildProgress(statusData.build_progress || 0);
          
          if (statusData.status === 'building') {
            const progressSteps = {
              10: 'Initializing build...',
              20: 'Provisioning VM...',
              40: 'Installing operating system...',
              60: 'Configuring tools...',
              75: 'Configuring OpenVPN...',
              90: 'Finalizing configuration...',
            };
            const stepMessage = Object.keys(progressSteps)
              .reverse()
              .find(step => statusData.build_progress >= step);
            setBuildStatus(progressSteps[stepMessage] || 'Building...');
          } else if (statusData.status === 'ready') {
            clearInterval(buildPollIntervalRef.current);
            buildPollIntervalRef.current = null;
            setIsBuilding(false);
            setBuildProgress(100);
            setBuildStatus('Agent built successfully! Ready to download.');
            
            setBuildResult(prev => ({ 
              ...prev, 
              ...statusData,
              status: 'ready'
            }));
            
            showNotif(`Agent ${result.agent_name} ready for download`, 'success');
          } else if (statusData.status === 'error') {
            clearInterval(buildPollIntervalRef.current);
            buildPollIntervalRef.current = null;
            setIsBuilding(false);
            setBuildStatus(`Error: ${statusData.status_message || 'Build failed'}`);
            setBuildResult(prev => ({ ...prev, ...statusData, status: 'error' }));
            showNotif(`Error building agent: ${statusData.status_message}`, 'error');
          }
        } catch (pollError) {
          console.error('Error polling agent status:', pollError);
        }
      }, 3000);
      
      // Safety timeout — only stop polling, never regress a completed build
      setTimeout(() => {
        if (buildPollIntervalRef.current) {
          clearInterval(buildPollIntervalRef.current);
          buildPollIntervalRef.current = null;
          // Only show warning if still in building state
          setIsBuilding(prev => {
            if (prev) setBuildStatus('Build taking longer than expected. Check agent status.');
            return prev;
          });
        }
      }, 600000);

    } catch (error) {
      console.error('Error building agent:', error);
      let errorMessage = 'Unknown error building agent';
      
      if (error.response) {
        errorMessage = error.response.data?.detail || error.response.data?.message || `Error ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'Could not connect to server';
      } else {
        errorMessage = error.message;
      }
      
      showNotif(`Error: ${errorMessage}`, 'error');
      setIsBuilding(false);
      setBuildStatus('');
      setBuildProgress(0);
    }
  };

  const handleAgentBuilt = (buildResult) => {
    if (onAgentBuilt) {
      onAgentBuilt(buildResult);
    }
    // Switch to manage tab and refresh agents list
    setActiveTab('manage');
    fetchAgents(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Modal Content */}
      <div className="relative w-full max-w-6xl max-h-[90vh] overflow-auto rounded-2xl bg-slate-900/90 shadow-2xl border border-slate-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Manage Agents</h3>
            <p className="text-sm text-slate-400 mt-1">{engagement.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'create'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Create Agent
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'manage'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Manage Agents ({agents.length})
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'create' && (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
            {/* Agent Name & Engagement Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Agent Name</label>
                <input 
                  type="text" 
                  className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  disabled={isBuilding}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Engagement</label>
                <div className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-400 text-sm">
                  {engagement.title}
                </div>
              </div>
            </div>

            {/* VM Configuration */}
            <div>
              <h4 className="text-sm font-semibold text-slate-200 mb-3">VM Configuration</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-2">Provider</label>
                  <select 
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                    value={vmProvider}
                    onChange={(e) => setVmProvider(e.target.value)}
                    disabled={isBuilding}
                  >
                    <option value="vmware">VMware</option>
                    <option value="virtualbox">VirtualBox</option>
                    <option value="proxmox">Proxmox</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-2">OS Template</label>
                  <select 
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                    value={osTemplate}
                    onChange={(e) => setOsTemplate(e.target.value)}
                    disabled={isBuilding}
                  >
                    <option value="kali">Kali Linux</option>
                    <option value="parrot">Parrot Security</option>
                    <option value="blackarch">BlackArch</option>
                    <option value="ubuntu">Ubuntu Server</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-2">CPU Cores</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="16" 
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                    value={cpuCores}
                    onChange={(e) => setCpuCores(parseInt(e.target.value))}
                    disabled={isBuilding}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-2">RAM (GB)</label>
                  <input 
                    type="number" 
                    min="2" 
                    max="64" 
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                    value={ramGb}
                    onChange={(e) => setRamGb(parseInt(e.target.value))}
                    disabled={isBuilding}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-2">Disk (GB)</label>
                  <input 
                    type="number" 
                    min="20" 
                    max="500" 
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                    value={diskGb}
                    onChange={(e) => setDiskGb(parseInt(e.target.value))}
                    disabled={isBuilding}
                  />
                </div>
              </div>
            </div>

            {/* Network Configuration */}
            <div>
              <h4 className="text-sm font-semibold text-slate-200 mb-3">Network Configuration</h4>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => setNetworkMode('dhcp')}
                    disabled={isBuilding}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      networkMode === 'dhcp'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-950/70 border border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    DHCP (Automatic)
                  </button>
                  <button
                    onClick={() => setNetworkMode('static')}
                    disabled={isBuilding}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      networkMode === 'static'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-950/70 border border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    Static IP
                  </button>
                </div>

                {networkMode === 'static' && (
                  <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-slate-950/50 border border-slate-800">
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">IP Address</label>
                      <input 
                        type="text" 
                        placeholder="192.168.1.100"
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                        value={staticIp}
                        onChange={(e) => setStaticIp(e.target.value)}
                        disabled={isBuilding}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Netmask</label>
                      <input
                        type="text"
                        placeholder="255.255.255.0 or /24"
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                        value={staticNetmask}
                        onChange={(e) => setStaticNetmask(e.target.value)}
                        disabled={isBuilding}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Gateway</label>
                      <input 
                        type="text" 
                        placeholder="192.168.1.1"
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                        value={staticGateway}
                        onChange={(e) => setStaticGateway(e.target.value)}
                        disabled={isBuilding}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">DNS Servers</label>
                      <input 
                        type="text" 
                        placeholder="8.8.8.8, 8.8.4.4"
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                        value={dnsServers}
                        onChange={(e) => setDnsServers(e.target.value)}
                        disabled={isBuilding}
                      />
                      <p className="text-xs text-slate-500 mt-1">Separar con comas</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* OpenVPN Configuration */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-200">OpenVPN Configuration</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={vpnEnabled}
                    onChange={(e) => setVpnEnabled(e.target.checked)}
                    disabled={isBuilding}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  <span className="ml-3 text-sm text-slate-400">Enable VPN</span>
                </label>
              </div>

              {vpnEnabled && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs text-slate-400 mb-2">VPN Server</label>
                      <input 
                        type="text" 
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                        value={vpnServer}
                        onChange={(e) => setVpnServer(e.target.value)}
                        disabled={isBuilding}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Port</label>
                      <input 
                        type="number" 
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                        value={vpnPort}
                        onChange={(e) => setVpnPort(parseInt(e.target.value))}
                        disabled={isBuilding}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Protocol</label>
                      <select 
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                        value={vpnProtocol}
                        onChange={(e) => setVpnProtocol(e.target.value)}
                        disabled={isBuilding}
                      >
                        <option value="tcp">TCP</option>
                        <option value="udp">UDP</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Assigned IP (tun0)</label>
                      {loadingVpnIps ? (
                        <div className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-400 text-sm">
                          Loading IPs...
                        </div>
                      ) : (
                        <select 
                          className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-600 focus:outline-none"
                          value={selectedVpnIp}
                          onChange={(e) => setSelectedVpnIp(e.target.value)}
                          disabled={isBuilding}
                        >
                          {availableVpnIps.map(ip => (
                            <option key={ip} value={ip}>{ip}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {availableVpnIps.length > 0 && (
                    <div className="text-xs text-slate-500 bg-slate-950/50 border border-slate-800 rounded-lg p-2">
                      <span className="text-slate-400">CCD Config:</span> ifconfig-push {selectedVpnIp} 255.255.255.0
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Agent Settings */}
            <div>
              <h4 className="text-sm font-semibold text-slate-200 mb-3">Agent Configuration</h4>
              <div className="space-y-2">
                <label className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-slate-800 cursor-pointer hover:border-slate-700">
                  <span className="text-sm text-slate-300">Auto-start with engagement</span>
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500"
                    checked={autoStart}
                    onChange={(e) => setAutoStart(e.target.checked)}
                    disabled={isBuilding}
                  />
                </label>
                <label className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-slate-800 cursor-pointer hover:border-slate-700">
                  <span className="text-sm text-slate-300">Persistent storage</span>
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500"
                    checked={persistentStorage}
                    onChange={(e) => setPersistentStorage(e.target.checked)}
                    disabled={isBuilding}
                  />
                </label>
                <label className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-slate-800 cursor-pointer hover:border-slate-700">
                  <span className="text-sm text-slate-300">Enable snapshots</span>
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500"
                    checked={snapshotsEnabled}
                    onChange={(e) => setSnapshotsEnabled(e.target.checked)}
                    disabled={isBuilding}
                  />
                </label>
              </div>
            </div>

            {/* Toolsets */}
            <div>
              <h4 className="text-sm font-semibold text-slate-200 mb-3">Toolsets</h4>
              <div className="space-y-2">
                {TOOL_PROFILES.map((profile) => (
                  <label
                    key={profile.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-slate-800 cursor-pointer hover:border-slate-700"
                  >
                    <span className="pr-4">
                      <span className="block text-sm text-slate-300">{profile.label}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">{profile.packages}</span>
                    </span>
                    <input
                      type="checkbox"
                      className="w-4 h-4 shrink-0 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500"
                      checked={toolProfiles.includes(profile.id)}
                      onChange={(e) => setToolProfiles(
                        e.target.checked
                          ? [...toolProfiles, profile.id]
                          : toolProfiles.filter((p) => p !== profile.id)
                      )}
                      disabled={isBuilding}
                    />
                  </label>
                ))}
              </div>
            </div>

            {/* Build Progress */}
            {isBuilding && (
              <div className="p-4 rounded-lg bg-slate-950/50 border border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-300">{buildStatus}</span>
                  <span className="text-sm text-indigo-400">{buildProgress}%</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500 ease-out"
                    style={{ width: `${buildProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* SSH Credentials Warning */}
            {buildResult && buildResult.ssh_password && (
              <div className="p-6 rounded-xl bg-rose-950/30 border-2 border-rose-500/50 shadow-lg">
                <div className="flex items-start gap-3 mb-4">
                  <svg className="w-6 h-6 text-rose-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-medium font-bold text-rose-400 mb-2">IMPORTANTE: Guarda estas credenciales AHORA</h4>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      These SSH credentials are shown <strong>ONCE ONLY</strong>. If lost, you must rebuild the agent.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-950/70 border border-rose-500/30 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Agent Name</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-base font-mono text-indigo-400 bg-slate-900/50 px-3 py-2 rounded border border-slate-700">
                        {buildResult.agent_name}
                      </code>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">SSH Usuario</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-base font-mono text-emerald-400 bg-slate-900/50 px-3 py-2 rounded border border-slate-700">
                        {buildResult.ssh_username || 'mindcore'}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(buildResult.ssh_username || 'mindcore');
                          showNotif('Username copied to clipboard', 'success');
                        }}
                        className="px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium"
                      >
                        Copiar
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-rose-400 uppercase tracking-wider">SSH Password (Save NOW!)</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-base font-mono text-rose-400 bg-slate-900/50 px-3 py-2 rounded border border-rose-500/50 font-bold">
                        {buildResult.ssh_password}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(buildResult.ssh_password);
                          setPasswordCopied(true);
                          showNotif('Password copied to clipboard', 'success');
                        }}
                        className={`px-3 py-2 rounded text-xs font-medium ${
                          passwordCopied 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-rose-600 hover:bg-rose-700 text-white'
                        }`}
                      >
                        {passwordCopied ? '✓ Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>

                  {buildResult.download_url && buildResult.status === 'ready' && (
                    <div className="pt-2 border-t border-slate-700">
                      <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Download VM</label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 text-xs font-mono text-slate-400 bg-slate-900/50 px-3 py-2 rounded border border-slate-700 truncate">
                          {buildResult.download_url}
                        </code>
                        <a
                          href={buildResult.download_url}
                          download
                          className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium flex items-center gap-2 whitespace-nowrap"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-3 mt-4 p-3 rounded-lg bg-slate-950/50 border border-slate-700 cursor-pointer hover:border-slate-600">
                  <input
                    type="checkbox"
                    checked={credentialsSaved}
                    onChange={(e) => setCredentialsSaved(e.target.checked)}
                    className="w-5 h-5 mt-0.5 text-emerald-600 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-slate-300 leading-relaxed">
                    I have saved the SSH credentials in a safe place
                  </span>
                </label>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              {buildResult && buildResult.ssh_password ? (
                <button
                  onClick={() => {
                    if (buildResult && buildResult.status === 'ready') {
                      handleAgentBuilt(buildResult);
                    }
                    setActiveTab('manage');
                    fetchAgents(true);
                  }}
                  disabled={!credentialsSaved}
                  className="px-6 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  title={!credentialsSaved ? "Please confirm you saved the credentials" : "Close and continue"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Continuar
                </button>
              ) : (
                <>
                  {isBuilding ? (
                    <button
                      onClick={() => {
                        setActiveTab('manage');
                        fetchAgents(true);
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                    >
                      Close
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setActiveTab('manage');
                          fetchAgents(true);
                        }}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleBuildAgent}
                        className="px-6 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Build Agent
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'manage' && (
          <div className="max-h-[70vh] overflow-y-auto pr-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <svg className="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12">
                <svg 
                  className="w-20 h-20 mx-auto text-slate-600 mb-4" 
                  style={{ fill: 'currentColor', overflow: 'hidden' }}
                  viewBox="0 0 1024 1024" 
                  version="1.1" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M683.7 922.7h-345c-73.5 0-133.3-59.8-133.3-133.3V459.8c0-73.5 59.8-133.3 133.3-133.3h345c73.5 0 133.3 59.8 133.3 133.3v329.6c0 73.5-59.8 133.3-133.3 133.3z m-345-506.9c-24.3 0-44.1 19.8-44.1 44.1v329.6c0 24.3 19.8 44.1 44.1 44.1h345c24.3 0 44.1-19.8 44.1-44.1V459.8c0-24.3-19.8-44.1-44.1-44.1h-345z" />
                  <path d="M914.3 759.6c-24.6 0-44.6-20-44.6-44.6V534.3c0-24.6 20-44.6 44.6-44.6s44.6 20 44.6 44.6V715c0 24.7-20 44.6-44.6 44.6zM111.7 759.6c-24.6 0-44.6-20-44.6-44.6V534.3c0-24.6 20-44.6 44.6-44.6s44.6 20 44.6 44.6V715c0 24.7-19.9 44.6-44.6 44.6z" />
                  <path d="M511.2 415.8c-24.6 0-44.6-20-44.6-44.6V239.3c0-24.6 20-44.6 44.6-44.6s44.6 20 44.6 44.6v131.9c0 24.6-20 44.6-44.6 44.6z" />
                  <path d="M511.2 276.6c-49.2 0-89.2-40-89.2-89.2s40-89.2 89.2-89.2 89.2 40 89.2 89.2-40 89.2-89.2 89.2z m0-89.2h0.2-0.2z m0 0h0.2-0.2z m0 0h0.2-0.2z m0 0h0.2-0.2z m0 0z m0 0h0.2-0.2z m0 0h0.2-0.2z m0-0.1h0.2-0.2z" />
                  <path d="M399 675.5c-28.1 0-50.9-22.8-50.9-50.9 0-28.1 22.8-50.9 50.9-50.9s50.9 22.8 50.9 50.9c0 28.1-22.8 50.9-50.9 50.9zM622.9 675.5c-28.1 0-50.9-22.8-50.9-50.9 0-28.1 22.8-50.9 50.9-50.9 28.1 0 50.9 22.8 50.9 50.9 0 28.1-22.8 50.9-50.9 50.9z" />
                </svg>
                <p className="text-slate-400 text-lg mb-2">No agents built</p>
                <p className="text-slate-500 text-sm">Build an agent first to see it here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agents.map((agent) => (
                  <div 
                    key={agent.id}
                    className="p-4 rounded-lg bg-slate-950/50 border border-slate-800 hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-base font-mono font-semibold text-indigo-400 truncate">
                            {agent.agent_name}
                          </h4>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(getDisplayStatus(agent))}`}>
                            {getStatusIcon(getDisplayStatus(agent))}
                            {getDisplayStatus(agent)}
                          </span>
                          {agent.build_progress < 100 && agent.status === 'building' && (
                            <span className="text-xs text-slate-400">
                              {agent.build_progress}%
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-slate-500 text-xs">Format:</span>
                            <div className="text-slate-300 font-medium uppercase">{agent.vm_format || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs">Size:</span>
                            <div className="text-slate-300 font-medium">{formatFileSize(agent.vm_file_size)}</div>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs">VPN IP:</span>
                            <div className="text-slate-300 font-mono text-xs">{agent.vpn_ip || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs">Built:</span>
                            <div className="text-slate-300 text-xs">{formatDate(agent.built_at)}</div>
                          </div>
                        </div>

                        <div className="mt-3 text-sm">
                          <span className="text-slate-500 text-xs">SHA256sum:</span>
                          <div className="mt-1 flex items-center gap-2">
                            <code 
                              className={`text-slate-300 font-mono text-xs break-all ${agent.vm_file_hash ? 'cursor-pointer hover:text-indigo-400 transition-colors' : ''}`}
                              onClick={agent.vm_file_hash ? () => toggleHashExpansion(agent.id) : undefined}
                              title={agent.vm_file_hash ? (expandedHashes.has(agent.id) ? "Click to collapse" : "Click to expand full hash") : "Not available for agents built before checksum feature"}
                            >
                              {formatHash(agent.vm_file_hash, agent.id)}
                            </code>
                            {agent.vm_file_hash && (
                              <button
                                onClick={() => toggleHashExpansion(agent.id)}
                                className="text-indigo-400 hover:text-indigo-300 text-xs font-medium transition-colors"
                                title={expandedHashes.has(agent.id) ? "Collapse" : "Expand"}
                              >
                                {expandedHashes.has(agent.id) ? '▼' : '▶'}
                              </button>
                            )}
                          </div>
                        </div>

                        {agent.last_seen && (
                          <div className="mt-2 text-xs text-slate-500">
                            Last seen: {formatDate(agent.last_seen)}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {agent.status !== 'building' && (
                          <button
                            onClick={() => handleDownload(agent)}
                            className="p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
                            title="Download agent"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                        )}

                        {agent.status !== 'building' && (
                          <button
                            onClick={() => {
                              setSelectedAgent(agent);
                              setShowDownloadLinkModal(true);
                            }}
                            className="p-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                            title="Generate download link"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setAgentToDelete(agent);
                            setShowDeleteConfirm(true);
                          }}
                          className="p-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white transition-colors"
                          title="Delete agent"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-800">
          <div className="text-sm text-slate-400">
            {activeTab === 'manage' && `${agents.length} agent${agents.length !== 1 ? 's' : ''} total`}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && agentToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)}></div>
          <div className="relative bg-slate-900 rounded-xl border border-slate-800 p-6 max-w-md w-full">
            <h4 className="text-lg font-semibold text-white mb-2">Delete Agent?</h4>
            <p className="text-sm text-slate-400 mb-4">
              Are you sure you want to delete agent <strong className="text-slate-300">{agentToDelete.agent_name}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setAgentToDelete(null);
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(agentToDelete)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-rose-600 text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Download Link Modal */}
      {showDownloadLinkModal && selectedAgent && (
        <GenerateDownloadLinkModal
          agent={selectedAgent}
          onClose={() => {
            setShowDownloadLinkModal(false);
            setSelectedAgent(null);
          }}
          onLinkGenerated={(linkData) => {
            console.log('Download link generated:', linkData);
          }}
        />
      )}

      {/* Notification Toast */}
      {showNotification && (
        <div className="fixed bottom-6 right-6 z-[60] animate-slide-in">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
            notificationType === 'success' 
              ? 'bg-emerald-600 text-white' 
              : 'bg-rose-600 text-white'
          }`}>
            {notificationType === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="text-sm font-medium">{notificationMessage}</span>
            <button onClick={() => setShowNotification(false)} className="ml-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagementConsolidatedModal;
